/**
 * Adapter: generator_v2-Schema (sentinus-course-v2) → generische Blöcke.
 *
 * EINZIGE Stelle, die das Backend-Schema kennt. Tauscht das Backend später
 * sein Format, ändert sich nur dieser Adapter — Renderer/Screens bleiben
 * block-nativ. Reihenfolge der Blöcke spiegelt das Kapitel-Layout:
 *   Text → Medien → Fragen → Downloads.
 */
import type {
  ChapterPayload,
  CourseManifest,
  ModuleSummary,
  ApiMedia,
} from "../api/types";
import { assetUrl } from "./assets";
import type {
  Block,
  Chapter,
  ChapterRef,
  Course,
  CourseModule,
  Question,
} from "./types";

function mediaToBlock(media: ApiMedia, idx: number): Block | null {
  const url = assetUrl((media.url as string) || "") || "";
  if (!url) return null;
  const type = (media.type || "").toLowerCase();
  const id = `media-${idx}`;
  if (type.includes("video"))
    return { id, type: "video", src: url, title: media.title as string };
  if (type.includes("audio") || type.includes("podcast"))
    return { id, type: "audio", src: url, title: media.title as string };
  if (type.includes("embed") || type.includes("iframe"))
    return { id, type: "embed", src: url, title: media.title as string };
  // Default: Bild
  return { id, type: "image", src: url, caption: media.title as string };
}

function questionsToBlock(payload: ChapterPayload): Block | null {
  const raw = payload.questions?.length ? payload.questions : payload.fragen;
  if (!raw?.length) return null;
  const questions: Question[] = raw.map((q, i) => ({
    id: q.id || `q-${i}`,
    text: q.text,
    hint: q.mitnehmen,
    own: Boolean(q.eigene),
  }));
  return { id: "questions", type: "questions", questions };
}

/** Ein rohes Kapitel (ChapterResponse.chapter) → App-Kapitel mit Blöcken. */
export function chapterToBlocks(payload: ChapterPayload, moduleId: string): Chapter {
  const blocks: Block[] = [];

  const html = payload.text_html || payload.text || "";
  if (html.trim()) blocks.push({ id: "text", type: "text", html });

  (payload.media ?? []).forEach((m, i) => {
    const b = mediaToBlock(m, i);
    if (b) blocks.push(b);
  });

  const q = questionsToBlock(payload);
  if (q) blocks.push(q);

  return {
    id: payload.id,
    moduleId,
    title: payload.title || payload.titel || "Kapitel",
    summary: payload.summary,
    layout: payload.layout || "lesson",
    image: assetUrl((payload.image as string) || (payload.bild as string)),
    blocks,
    // Roh-Payload mitführen: Werkstatt-Kapitel (layout=projekt) rendern aus
    // steckbrief/schritte/material — die API liefert den vollen Block (dict(block)).
    raw: payload as Record<string, unknown>,
  };
}

function moduleToAppModule(m: ModuleSummary): CourseModule {
  const chapters: ChapterRef[] = (m.chapters ?? []).map((c) => ({
    id: c.id,
    title: c.title || c.titel || "Kapitel",
    summary: c.summary,
    durationMinutes: c.duration_minutes,
    layout: c.layout || "lesson",
    questionCount: c.question_count ?? 0,
    mediaCount: c.media_count ?? 0,
    image: assetUrl(c.image),
  }));
  const intro = m.intro
    ? {
        headline: m.intro.headline,
        textHtml: m.intro.text_html,
        ctaLabel: m.intro.cta_label,
        image: assetUrl(m.intro.image || null),
        video: m.intro.video
          ? {
              src: assetUrl(m.intro.video.src || "") || undefined,
              poster: assetUrl(m.intro.video.poster || "") || undefined,
              caption: m.intro.video.caption,
            }
          : null,
      }
    : null;
  return {
    id: m.id,
    number: m.number,
    menueNr: m.menue_nr ?? null,
    title: m.title || m.titel || "Modul",
    summary: m.summary,
    intro,
    group: m.gruppe || "teilnehmer",
    image: assetUrl(m.image),
    download: m.download ?? null,
    chapters,
  };
}

/**
 * Manifest → App-Kurs. Die deklarierten Modi werden aus den Daten abgeleitet:
 * „normal" immer, „fragen" nur wenn irgendein Kapitel Fragen hat. So zeigt ein
 * Kurs ohne Fragen automatisch keinen Fragen-Modus (Konzept §6, Punkt 2).
 * IDs 1:1 zur Web-App (`ki-kurs-modus-v2` = "fragen"|"normal").
 */
export function manifestToCourse(manifest: CourseManifest): Course {
  const modules = (manifest.modules ?? []).map(moduleToAppModule);
  const hasQuestions = modules.some((m) =>
    m.chapters.some((c) => c.questionCount > 0),
  );
  const modes = ["normal", ...(hasQuestions ? ["fragen"] : [])];
  return {
    id: manifest.course_id || manifest.id,
    title: manifest.titel || manifest.title,
    version: manifest.version,
    stand: manifest.stand,
    modes,
    modules,
  };
}
